import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Image as ImageIcon, Link2, Save, SlidersHorizontal, Trash2 } from 'lucide-react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';

import { Button } from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/supabase';

type QrRoute = Database['public']['Tables']['qr_routes']['Row'];

const QR_SIZE = 320;
const DEFAULT_LOGO_SIZE = 24;

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function AdminQrGenerator() {
  const [routes, setRoutes] = useState<QrRoute[]>([]);
  const [label, setLabel] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [useTrackingRoute, setUseTrackingRoute] = useState(true);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoAspect, setLogoAspect] = useState(1);
  const [logoSizePercent, setLogoSizePercent] = useState(DEFAULT_LOGO_SIZE);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const trackingUrl = slug ? `${baseUrl}/q/${slug}` : '';
  const qrValue = useTrackingRoute ? trackingUrl : targetUrl;

  const logoDimensions = useMemo(() => {
    const maxSize = (QR_SIZE * logoSizePercent) / 100;
    if (logoAspect >= 1) {
      return {
        width: maxSize,
        height: maxSize / logoAspect,
      };
    }
    return {
      width: maxSize * logoAspect,
      height: maxSize,
    };
  }, [logoAspect, logoSizePercent]);

  const imageSettings = useMemo(() => {
    if (!logoDataUrl) return undefined;
    const x = (QR_SIZE - logoDimensions.width) / 2 + offsetX;
    const y = (QR_SIZE - logoDimensions.height) / 2 + offsetY;
    return {
      src: logoDataUrl,
      height: logoDimensions.height,
      width: logoDimensions.width,
      x,
      y,
      excavate: true,
    };
  }, [logoDataUrl, logoDimensions.height, logoDimensions.width, offsetX, offsetY]);

  const loadRoutes = async () => {
    const { data, error } = await supabase
      .from('qr_routes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[AdminQrGenerator] Failed to load routes:', error);
      return;
    }

    setRoutes(data || []);
  };

  useEffect(() => {
    loadRoutes();
  }, []);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString();
      if (!result) return;
      const img = new Image();
      img.onload = () => {
        setLogoAspect(img.width / img.height || 1);
        setLogoDataUrl(result);
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setErrorMessage(null);
    if (!slug || !targetUrl) {
      setErrorMessage('Slug and target URL are required.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        slug,
        label: label.trim() || null,
        target_url: targetUrl.trim(),
        is_active: isActive,
        updated_at: new Date().toISOString(),
      };

      if (editingId) {
        const { error } = await supabase.from('qr_routes').update(payload).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('qr_routes').insert(payload);
        if (error) throw error;
      }

      await loadRoutes();
    } catch (error) {
      console.error('[AdminQrGenerator] Failed to save QR route:', error);
      setErrorMessage('Failed to save QR route.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (route: QrRoute) => {
    setEditingId(route.id);
    setSlug(route.slug);
    setLabel(route.label || '');
    setTargetUrl(route.target_url);
    setIsActive(route.is_active);
    setUseTrackingRoute(true);
  };

  const handleDelete = async (routeId: string) => {
    const confirmed = window.confirm('Delete this QR route?');
    if (!confirmed) return;

    const { error } = await supabase.from('qr_routes').delete().eq('id', routeId);
    if (error) {
      console.error('[AdminQrGenerator] Failed to delete route:', error);
      setErrorMessage('Failed to delete QR route.');
      return;
    }

    if (editingId === routeId) setEditingId(null);
    await loadRoutes();
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      setErrorMessage('Failed to copy.');
    }
  };

  const downloadSvg = () => {
    if (!svgRef.current || !qrValue) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svgRef.current);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slug || 'qr-code'}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadPng = () => {
    if (!canvasRef.current || !qrValue) return;
    const url = canvasRef.current.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slug || 'qr-code'}.png`;
    link.click();
  };

  const resetForm = () => {
    setSlug('');
    setLabel('');
    setTargetUrl('');
    setIsActive(true);
    setUseTrackingRoute(true);
    setEditingId(null);
    setLogoDataUrl(null);
    setLogoAspect(1);
    setLogoSizePercent(DEFAULT_LOGO_SIZE);
    setOffsetX(0);
    setOffsetY(0);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[#EBFFEE] mb-2">Route QR Generator</h1>
        <p className="text-[#A9A3A3]">Create tracked QR codes for any route or URL.</p>
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
        <div className="bg-[#221F1F] border border-[#504A4A] rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Link2 className="w-5 h-5 text-rollops-accent" />
            QR Settings
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A9A3A3] mb-2">Slug *</label>
              <input
                value={slug}
                onChange={(event) => setSlug(slugify(event.target.value))}
                className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
                placeholder="event-landing"
              />
              <button
                type="button"
                onClick={() => setSlug(slugify(label || `qr-${Date.now()}`))}
                className="text-xs text-rollops-accent mt-2"
              >
                Generate from label
              </button>
            </div>
            <div>
              <label className="block text-sm text-[#A9A3A3] mb-2">Label</label>
              <input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
                placeholder="Affiliate Program QR"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Target URL *</label>
            <input
              value={targetUrl}
              onChange={(event) => setTargetUrl(event.target.value)}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
              placeholder="https://rollops.pro/affiliate-program"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-[#A9A3A3]">
            <input
              type="checkbox"
              checked={useTrackingRoute}
              onChange={(event) => setUseTrackingRoute(event.target.checked)}
              className="rounded border-[#504A4A] bg-[#221F1F]"
            />
            Use tracking route (/q/:slug)
          </label>

          <label className="flex items-center gap-2 text-sm text-[#A9A3A3]">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              className="rounded border-[#504A4A] bg-[#221F1F]"
            />
            Active
          </label>

          <div className="border-t border-[#504A4A] pt-4 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <ImageIcon className="w-5 h-5 text-rollops-accent" />
              Logo overlay
            </div>
            <input type="file" accept="image/*" onChange={handleLogoUpload} />

            {logoDataUrl && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-sm text-[#A9A3A3]">
                  <SlidersHorizontal className="w-4 h-4" />
                  Logo size ({logoSizePercent}%)
                </div>
                <input
                  type="range"
                  min={10}
                  max={40}
                  value={logoSizePercent}
                  onChange={(event) => setLogoSizePercent(Number(event.target.value))}
                  className="w-full"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#A9A3A3] mb-2 block">Offset X</label>
                    <input
                      type="range"
                      min={-80}
                      max={80}
                      value={offsetX}
                      onChange={(event) => setOffsetX(Number(event.target.value))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#A9A3A3] mb-2 block">Offset Y</label>
                    <input
                      type="range"
                      min={-80}
                      max={80}
                      value={offsetY}
                      onChange={(event) => setOffsetY(Number(event.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
                <Button type="button" className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]" onClick={() => {
                  setOffsetX(0);
                  setOffsetY(0);
                  setLogoSizePercent(DEFAULT_LOGO_SIZE);
                }}>
                  Reset positioning
                </Button>
              </div>
            )}
          </div>

          {errorMessage && <p className="text-sm text-[#F87171]">{errorMessage}</p>}

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : editingId ? 'Update QR' : 'Save QR'}
            </Button>
            <Button type="button" className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]" onClick={resetForm}>
              Clear
            </Button>
          </div>
        </div>

        <div className="bg-[#221F1F] border border-[#504A4A] rounded-2xl p-6 space-y-4">
          <div className="text-lg font-semibold text-[#EBFFEE]">Preview</div>
          {qrValue ? (
            <div className="flex flex-col items-center gap-4">
              <QRCodeSVG
                value={qrValue}
                size={QR_SIZE}
                bgColor="#ffffff"
                fgColor="#111010"
                includeMargin
                imageSettings={imageSettings}
                ref={svgRef}
              />
              <QRCodeCanvas
                value={qrValue}
                size={QR_SIZE}
                bgColor="#ffffff"
                fgColor="#111010"
                includeMargin
                imageSettings={imageSettings}
                ref={canvasRef}
                style={{ display: 'none' }}
              />
              <div className="text-xs text-[#A9A3A3] break-all text-center">{qrValue}</div>
              <div className="flex flex-wrap gap-3">
                <Button type="button" className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]" onClick={downloadSvg}>
                  Download SVG
                </Button>
                <Button type="button" className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]" onClick={downloadPng}>
                  Download PNG
                </Button>
                <Button type="button" className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]" onClick={() => handleCopy(qrValue)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy URL
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#A9A3A3]">Enter a slug and target URL to preview.</div>
          )}
        </div>
      </div>

      <section className="space-y-4">
        <div className="text-lg font-semibold text-[#EBFFEE]">Saved QR routes</div>
        {routes.length === 0 ? (
          <div className="text-sm text-[#A9A3A3]">No QR routes saved yet.</div>
        ) : (
          <div className="space-y-3">
            {routes.map((route) => (
              <div
                key={route.id}
                className="bg-[#221F1F] border border-[#504A4A] rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
              >
                <div>
                  <div className="text-[#EBFFEE] font-semibold">{route.label || route.slug}</div>
                  <div className="text-xs text-[#A9A3A3]">{`${baseUrl}/q/${route.slug}`}</div>
                  <div className="text-xs text-[#706A6A]">{route.target_url}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]"
                    onClick={() => handleEdit(route)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]"
                    onClick={() => handleCopy(`${baseUrl}/q/${route.slug}`)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button
                    type="button"
                    className="bg-[#1A1818] hover:bg-[#363232] text-[#EBFFEE] border border-[#504A4A]"
                    onClick={() => handleDelete(route.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
